import { useAppDispatch } from '@hooks/redux-custom';
import { AvaStore, JimboResponse } from '@models';
import { Autocomplete, Button, Dialog, DialogContent, Grid, IconButton, Paper, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useUpdateStoresListMutation } from '@services';
import { showToast } from '@store/slices/notification';
import { FC, useEffect, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { addErrorToLog } from '@store/slices/error-log';
import { findDeletedAndNewStores, formatStoreClusters } from './AvaDepotClusteringScreen.service';

interface Cluster {
  clusterID: string;
  clusterName: string;
  stores?: Store[];
}

interface Store {
  id: string;
  name: string;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedCluster: Cluster;
  storesInState: AvaStore[];
};

export const AvaDepotClustersEdit: FC<Props> = ({ isOpen, onClose, selectedCluster, storesInState }) => {
  const [store, setStore] = useState<string>();
  const [rows, setRows] = useState<Store[]>([]);
  const [updatedStores, setStores] = useState<Store[]>([]);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [updateStores] = useUpdateStoresListMutation();

  const dispatch = useAppDispatch();

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'Store Code', flex: 1 },
    {
      field: 'name',
      headerName: 'Store Name',
      flex: 2,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => {
        return (
          <IconButton onClick={() => handleRemoveStore(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        );
      },
    },
  ];

  const getStoreName = useMemo(() => {
    return (storeCode: string): string => {
      const store = storesInState && storesInState?.find((store) => store.store_code === storeCode);

      return store?.name || 'N/A';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesInState]);

  const handleAddStore = (storeCode: string | undefined) => {
    if (storeCode) {
      const isStoreExists = rows.some((row) => row.id === storeCode);

      if (!isStoreExists) {
        const newStore: Store = {
          id: storeCode,
          name: getStoreName(storeCode),
        };

        setRows((prevRows) => [...prevRows, newStore]);
        setStores((prevRows) => [...prevRows, newStore]);
        setUnsavedChanges(true);
      }
    }
  };

  const handleRemoveStore = (storeCode: string) => {
    if (storeCode) {
      const updatedRows = rows.filter((row) => String(row.id) !== String(storeCode));
      const updatedStore = updatedStores.filter((store) => String(store.id) !== String(storeCode));

      setRows(updatedRows);
      setStores(updatedStore);
      setUnsavedChanges(true);
    }
  };

  useEffect(() => {
    if (selectedCluster && selectedCluster.stores?.length) {
      setRows(
        selectedCluster.stores.map((row) => ({
          ...row,
        })),
      );

      setStores(selectedCluster.stores);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    const { deletedStores, newStores } = findDeletedAndNewStores(selectedCluster, updatedStores, storesInState);

    const formattedStors = formatStoreClusters(deletedStores, newStores, selectedCluster.clusterID, selectedCluster.clusterName);

    if (formattedStors.length) {
      updateStores(formattedStors)
        .unwrap()
        .then((response) => {
          dispatch(
            showToast({
              message: response + ' for cluster ' + selectedCluster.clusterName,
              type: 'success',
            }),
          );
        })
        .then(onClose)
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: 'Failed to update stores for cluster ' + selectedCluster.clusterName,
              type: 'error',
            }),
          );

          onClose();
        });
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md" data-testid="depot-cluster-edit-modal">
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', pb: 0, mt: 2 }}>
        {storesInState && (
          <>
            <Grid container alignItems="center" spacing={2} py={2}>
              <Grid item xs={10}>
                <Autocomplete
                  id="store-search"
                  data-testid="store-searchbar"
                  onChange={(e: any, newValue: AvaStore | null) => {
                    setStore(newValue?.store_code ?? '');
                  }}
                  options={storesInState}
                  getOptionLabel={(option) => `${option?.name} ${option?.store_code}` ?? ''}
                  disablePortal
                  renderInput={(params) => <TextField {...params} label="Search by store name or store code" />}
                />
              </Grid>
              <Grid item>
                <Button variant="contained" type="submit" onClick={() => handleAddStore(store)} data-testid="add-store-button">
                  Add
                </Button>
              </Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2} py={2}>
              <Grid item xs={12}>
                <Paper sx={{ height: '60vh' }}>
                  {rows && (
                    <DataGrid
                      sortingOrder={['desc', 'asc']}
                      rows={rows}
                      columns={columns}
                      disableSelectionOnClick
                      disableColumnSelector
                      disableColumnFilter
                      sx={{
                        '.MuiDataGrid-columnSeparator': {
                          display: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#CD9BF0',
                          color: '#161616',
                        },
                        '& .MuiDataGrid-row': {
                          '&:hover': {
                            backgroundColor: '#faf2ff',
                          },
                        },
                      }}
                    />
                  )}
                </Paper>
              </Grid>
              <Grid item>
                <Button variant="contained" onClick={handleSave} disabled={!unsavedChanges}>
                  Save
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
